import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, NavLink, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Button,
  Drawer,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  IconButton,
  Typography,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import Home from '@mui/icons-material/Home';
import People from '@mui/icons-material/People';
import Article from '@mui/icons-material/Article';
import PendingActions from '@mui/icons-material/PendingActions';
import Notifications from '@mui/icons-material/Notifications';
import Settings from '@mui/icons-material/Settings';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import Logout from '@mui/icons-material/Logout';
import Visibility from '@mui/icons-material/Visibility';
import Groups from '@mui/icons-material/Groups';
import Campaign from '@mui/icons-material/Campaign';
import Send from '@mui/icons-material/Send';
import History from '@mui/icons-material/History';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import Handshake from '@mui/icons-material/Handshake';
import { logout } from '../../store/slices/authSlice';

const DRAWER_WIDTH = 260;

const menuByRole = {
  admin: [
    { to: '/dashboard', label: 'Accueil', icon: Home },
    { to: '/dashboard/announcement-events', label: 'Annonces et Événements', icon: CampaignOutlined },
    { to: '/dashboard/partnerships', label: 'Partenariats', icon: Handshake },
    { to: '/dashboard/users', label: 'Utilisateurs', icon: People },
    { to: '/dashboard/posts', label: 'Articles', icon: Article },
    { to: '/dashboard/pending', label: 'En consultation', icon: PendingActions },
    { to: '/dashboard/notifications', label: 'Notifications', icon: Notifications },
    { to: '/dashboard/settings', label: 'Paramètres', icon: Settings },
  ],
  moderator: [
    { to: '/dashboard', label: 'Accueil', icon: Home },
    { to: '/dashboard/announcement-events', label: 'Annonces et Événements', icon: CampaignOutlined },
    { to: '/dashboard/partnerships', label: 'Partenariats', icon: Handshake },
    { to: '/dashboard/posts', label: 'Articles', icon: Article },
    { to: '/dashboard/pending', label: 'En consultation', icon: PendingActions },
    { to: '/dashboard/notifications', label: 'Notifications', icon: Notifications },
    { to: '/dashboard/settings', label: 'Paramètres', icon: Settings },
  ],
  professor: [
    { to: '/dashboard', label: 'Accueil', icon: Home },
    { to: '/dashboard/posts', label: 'Mes articles', icon: Article },
    { to: '/dashboard/posts/new', label: 'Créer un article', icon: AddCircleOutline },
    { to: '/dashboard/settings', label: 'Paramètres', icon: Settings },
  ],
  student: [
    { to: '/dashboard', label: 'Accueil', icon: Home },
    { to: '/dashboard/posts', label: 'Mes articles', icon: Article },
    { to: '/dashboard/posts/new', label: 'Créer un article', icon: AddCircleOutline },
    { to: '/dashboard/settings', label: 'Paramètres', icon: Settings },
  ],
  'space-student': [
    { to: '/dashboard', label: 'Accueil', icon: Home },
    { to: '/dashboard/announcement-events', label: 'Annonces et Événements', icon: CampaignOutlined },
    { to: '/dashboard/posts', label: 'Mes articles', icon: Article },
    { to: '/dashboard/posts/new', label: 'Créer un article', icon: AddCircleOutline },
    { to: '/dashboard/settings', label: 'Paramètres', icon: Settings },
  ],
};

const alumniMenuItems = [
  { to: '/dashboard/alumni', label: 'Données Alumni', icon: Groups },
  { to: '/dashboard/newsletters', label: 'Newsletters', icon: Campaign },
  { to: '/dashboard/alumni/send', label: 'Envoyer un e-mail', icon: Send },
  { to: '/dashboard/alumni/history', label: 'Historique', icon: History },
];

const PAGE_TRANSITION = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] },
};

export default function DashboardLayout() {
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showNavProgress, setShowNavProgress] = useState(false);
  const prevPathRef = useRef(null);
  const hideProgressRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const role = user?.role || 'student';
  const menuItems = menuByRole[role] || menuByRole.student;

  useEffect(() => {
    if (prevPathRef.current !== null && prevPathRef.current !== location.pathname) {
      setShowNavProgress(true);
      if (hideProgressRef.current) clearTimeout(hideProgressRef.current);
      hideProgressRef.current = setTimeout(() => {
        setShowNavProgress(false);
        hideProgressRef.current = null;
      }, 500);
    }
    prevPathRef.current = location.pathname;
    return () => {
      if (hideProgressRef.current) clearTimeout(hideProgressRef.current);
    };
  }, [location.pathname]);

  const handleLogout = () => {
    setAnchorEl(null);
    dispatch(logout());
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: '#1976d2',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}
      >
        <Toolbar>
          <IconButton color="inherit" onClick={() => setOpen(!open)} edge="start" sx={{ mr: 1 }}>
            {open ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Université de Ngozi
          </Typography>
          <Button
            component={Link}
            to="/"
            startIcon={<Visibility />}
            sx={{ color: 'white', mr: 1, textTransform: 'none' }}
          >
            Voir en tant que visiteur
          </Button>
          <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar
              src={
                user?.profilePicture
                  ? user.profilePicture.startsWith('http')
                    ? user.profilePicture
                    : `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${user.profilePicture}`
                  : undefined
              }
              sx={{ width: 36, height: 36, bgcolor: 'white', color: '#1976d2' }}
            >
              {(user?.fullname || 'U').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>
      {showNavProgress && (
        <LinearProgress
          color="primary"
          variant="indeterminate"
          sx={{
            position: 'fixed',
            top: 64,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            height: 4,
            '& .MuiLinearProgress-bar1Indeterminate, & .MuiLinearProgress-bar2Indeterminate': {
              animationDuration: '1.8s',
            },
          }}
        />
      )}
      <Drawer
        variant="persistent"
        open={open}
        sx={{
          width: open ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            top: 64,
            borderRight: '1px solid #e2e8f0',
            bgcolor: '#fff',
            transition: (theme) => theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }),
          },
        }}
      >
        <Box sx={{ overflow: 'auto', py: 2 }}>
          <List disablePadding>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to || (item.to !== '/dashboard' && location.pathname.startsWith(item.to));
              return (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  selected={isActive}
                  sx={{
                    mx: 1,
                    borderRadius: 2,
                    mb: 0.5,
                    '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: isActive ? 'inherit' : 'text.secondary' }}>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItemButton>
              );
            })}
            {(role === 'admin' || role === 'moderator') && (
              <>
                <ListSubheader sx={{ bgcolor: 'transparent', px: 2, pt: 2 }}>Alumni et newsletters</ListSubheader>
                {alumniMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.to;
                  return (
                    <ListItemButton
                      key={item.to}
                      component={NavLink}
                      to={item.to}
                      selected={isActive}
                      sx={{
                        mx: 1,
                        borderRadius: 2,
                        mb: 0.5,
                        '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: isActive ? 'inherit' : 'text.secondary' }}>
                        <Icon />
                      </ListItemIcon>
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.95rem' }} />
                    </ListItemButton>
                  );
                })}
              </>
            )}
          </List>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          ml: open ? 0 : 0,
          transition: 'margin-left 0.2s',
          minHeight: 'calc(100vh - 64px)',
          bgcolor: '#f8fafc',
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={PAGE_TRANSITION.initial}
            animate={PAGE_TRANSITION.animate}
            exit={PAGE_TRANSITION.exit}
            transition={PAGE_TRANSITION.transition}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </Box>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={
              user?.profilePicture
                ? user.profilePicture.startsWith('http')
                  ? user.profilePicture
                  : `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${user.profilePicture}`
                : undefined
            }
            sx={{ width: 40, height: 40, bgcolor: 'primary.main', color: 'white' }}
          >
            {(user?.fullname || 'U').charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle2">{user?.fullname}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">{user?.email}</Typography>
          </Box>
        </Box>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          Déconnexion
        </MenuItem>
      </Menu>
    </Box>
  );
}
